/**
 * Saved searches: a Library search (text, filters, advanced search, sort, view)
 * kept per person under a name. Private to the person who saved it.
 */
import { z } from 'zod';
import { AdvancedQuerySchema } from './advanced-search.ts';
import { RIGHTS_FILTERS } from './rights.ts';

export const ADDED_FILTERS = ['today', 'week', 'month'] as const;
export type AddedFilter = (typeof ADDED_FILTERS)[number];
export const ADDED_LABELS: Record<AddedFilter, string> = { today: 'Today', week: 'This week', month: 'This month' };

/** Quick filter on whether an asset's metadata is complete (no visible Needed or Required field is empty). */
export const METADATA_FILTERS = ['incomplete', 'complete'] as const;
export type MetadataFilter = (typeof METADATA_FILTERS)[number];
export const METADATA_FILTER_LABELS: Record<MetadataFilter, string> = { incomplete: 'Incomplete', complete: 'Complete' };

/** Quick filter on whether an asset's original can be read (0.29.0: archived means only in an archive tier). */
export const AVAILABILITY_FILTERS = ['online', 'archived', 'restoring', 'offline'] as const;
export type AvailabilityFilter = (typeof AVAILABILITY_FILTERS)[number];
export const AVAILABILITY_FILTER_LABELS: Record<AvailabilityFilter, string> = { online: 'Online', archived: 'Archived', restoring: 'Restoring', offline: 'Offline' };

/** Quick filter on automated QC (1.0.0-rc.8): the current version's latest QC. */
export const QC_FILTERS = ['passed', 'warnings', 'failed', 'not_run'] as const;
export type QcFilter = (typeof QC_FILTERS)[number];
export const QC_FILTER_LABELS: Record<QcFilter, string> = { passed: 'Passed', warnings: 'Warnings', failed: 'Failed', not_run: 'Not run' };

export const SavedSearchQuerySchema = z.object({
  q: z.string().max(500).default(''),
  status: z.array(z.string().max(80)).max(20).default([]),
  kind: z.array(z.string().max(80)).max(20).default([]),
  location: z.array(z.string().max(80)).max(50).default([]),
  category: z.array(z.string().max(80)).max(50).default([]),
  fields: z.record(z.string().max(80), z.array(z.string().max(500)).max(100)).default({}),
  added: z.enum(ADDED_FILTERS).nullable().default(null),
  metadata: z.enum(METADATA_FILTERS).nullable().default(null),
  availability: z.enum(AVAILABILITY_FILTERS).nullable().default(null),
  /** Absent in searches saved before 1.0.0-rc.8. */
  qc: z.enum(QC_FILTERS).nullable().optional(),
  /** 1.2.0: master tags (masters.ts MASTER_TAG: v:<variant>, f:<format class>, m:<variant>:<class>); absent before. */
  masters: z.array(z.string().max(80)).max(20).optional(),
  /** 1.8.0: the Rights quick filter (rights.ts RIGHTS_FILTERS); absent before. */
  rights: z.enum(RIGHTS_FILTERS).nullable().optional(),
  includeDismissed: z.boolean().default(false),
  advanced: AdvancedQuerySchema.nullable().default(null),
  sort: z.enum(['newest', 'oldest', 'title', 'duration', 'size', 'group_order']).default('newest'),
  view: z.enum(['grid', 'table']).default('grid'),
});
export type SavedSearchQuery = z.infer<typeof SavedSearchQuerySchema>;

export const SavedSearchInput = z.object({
  name: z.string().trim().min(1).max(120),
  query: SavedSearchQuerySchema,
});

export interface SavedSearchDTO {
  id: string;
  name: string;
  query: SavedSearchQuery;
  createdAt: string;
  updatedAt: string;
}
